import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { IColumn } from "../../types/ColumnTypes";
import { RootState } from "../store";

// Column list cache. `cachedForEmail` guards against serving one user's
// content to another (logged-out users get teaser/paywall variants);
// `currentPage` is the real pagination cursor — usePagination's internal
// counter resets on every mount and cannot be trusted with a warm cache.
interface ColumnState {
  columns: IColumn[];
  hasMore: boolean;
  status: "idle" | "loading" | "failed";
  cachedForEmail: string;
  currentPage: number;
  lastFetched: number | null;
}

const initialState: ColumnState = {
  columns: [],
  hasMore: true,
  status: "idle",
  cachedForEmail: "",
  currentPage: 1,
  lastFetched: null,
};

const dedupeById = (items: IColumn[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (!item?.id) return true;
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

export const columnReducer = createSlice({
  name: "column",
  initialState,
  reducers: {
    // Replace the cache with a fresh page-1 result.
    setColumns: (
      state,
      action: PayloadAction<{ columns: IColumn[]; email: string; page?: number }>
    ) => {
      state.columns = action.payload.columns;
      state.cachedForEmail = action.payload.email;
      state.currentPage = action.payload.page ?? 1;
      state.status = "idle";
      state.lastFetched = Date.now();
    },
    appendColumns: (
      state,
      action: PayloadAction<{ columns: IColumn[]; page: number }>
    ) => {
      state.columns = dedupeById([...state.columns, ...action.payload.columns]);
      state.currentPage = action.payload.page;
      state.status = "idle";
      state.lastFetched = Date.now();
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    // Background revalidation found nothing new.
    touchColumns: (state) => {
      state.lastFetched = Date.now();
      state.status = "idle";
    },
    setColumnsLoading: (state) => {
      state.status = "loading";
    },
    setColumnsFailed: (state) => {
      state.status = "failed";
    },
    clearColumns: () => initialState,
  },
});

export const {
  setColumns,
  appendColumns,
  setHasMore,
  touchColumns,
  setColumnsLoading,
  setColumnsFailed,
  clearColumns,
} = columnReducer.actions;

export const selectColumn = (state: RootState) => state.column;
export const selectColumns = (state: RootState) => state.column.columns;
export const selectColumnsHasMore = (state: RootState) => state.column.hasMore;
export const selectColumnsStatus = (state: RootState) => state.column.status;

export default columnReducer.reducer;
